import { useEffect, useMemo, useRef, useState } from 'react';
import { getDocumentDownloadUrl } from '../api/documents';

interface Props {
  attachmentDocumentId: string;
  durationSeconds?: number;
  isMine?: boolean;
}

function formatDuration(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const VOICE_NOTE_PLAY_EVENT = 'ecregi:voice-note-play';

// Generate pseudo-consistent waveform bar heights based on document id
function generateWaveformBars(seedStr: string, count = 28): number[] {
  let hash = 0;
  for (let i = 0; i < seedStr.length; i++) {
    hash = (hash << 5) - hash + seedStr.charCodeAt(i);
    hash |= 0;
  }
  const bars: number[] = [];
  for (let i = 0; i < count; i++) {
    // Generate height between 0.2 and 1.0
    const val = Math.abs(Math.sin(hash + i * 1.7) * 0.8) + 0.2;
    bars.push(Math.round(val * 100) / 100);
  }
  return bars;
}

export function VoiceNotePlayer({
  attachmentDocumentId,
  durationSeconds,
  isMine = false,
}: Props) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(durationSeconds || 0);
  const [playbackRate, setPlaybackRate] = useState<number>(1);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const waveformRef = useRef<HTMLDivElement>(null);

  const waveformBars = useMemo(
    () => generateWaveformBars(attachmentDocumentId, 32),
    [attachmentDocumentId],
  );

  // Stop playback if another voice note starts playing, or on unmount
  useEffect(() => {
    const handleOtherPlay = (e: Event) => {
      const detail = (e as CustomEvent<{ id: string }>).detail;
      if (detail && detail.id !== attachmentDocumentId) {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        setIsPlaying(false);
      }
    };

    window.addEventListener(VOICE_NOTE_PLAY_EVENT, handleOtherPlay);
    return () => {
      window.removeEventListener(VOICE_NOTE_PLAY_EVENT, handleOtherPlay);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
    };
  }, [attachmentDocumentId]);

  async function initAudio(): Promise<HTMLAudioElement | null> {
    if (audioRef.current) return audioRef.current;

    setIsLoading(true);
    setLoadError(false);

    try {
      let url = audioUrl;
      if (!url) {
        const docRes = await getDocumentDownloadUrl(attachmentDocumentId);
        url = docRes.url;
        setAudioUrl(url);
      }

      const audio = new Audio(url);
      audio.playbackRate = playbackRate;

      audio.addEventListener('loadedmetadata', () => {
        if (audio.duration && Number.isFinite(audio.duration) && audio.duration > 0) {
          setTotalDuration(Math.round(audio.duration));
        }
      });

      audio.addEventListener('timeupdate', () => {
        setCurrentTime(audio.currentTime);
      });

      audio.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });

      audio.addEventListener('error', () => {
        setIsPlaying(false);
        setIsLoading(false);
        setLoadError(true);
      });

      audioRef.current = audio;
      setIsLoading(false);
      return audio;
    } catch (err) {
      console.error('Failed to load voice note audio:', err);
      setIsLoading(false);
      setLoadError(true);
      return null;
    }
  }

  async function togglePlay() {
    if (loadError) {
      setLoadError(false);
      setAudioUrl(null);
      audioRef.current = null;
    }

    let audio = audioRef.current;
    if (!audio) {
      audio = await initAudio();
      if (!audio) return;
    }

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      try {
        // Stop any other currently playing voice note across the chat
        window.dispatchEvent(
          new CustomEvent(VOICE_NOTE_PLAY_EVENT, {
            detail: { id: attachmentDocumentId },
          }),
        );
        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        console.error('Playback error:', err);
        setIsPlaying(false);
      }
    }
  }

  function handleScrub(e: React.MouseEvent<HTMLDivElement>) {
    const el = waveformRef.current;
    if (!el || totalDuration <= 0) return;
    const rect = el.getBoundingClientRect();
    const clickX = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    const fraction = clickX / rect.width;
    const targetTime = fraction * totalDuration;

    setCurrentTime(targetTime);
    if (audioRef.current) {
      audioRef.current.currentTime = targetTime;
    } else {
      // Lazy init and seek
      initAudio().then((audio) => {
        if (audio) {
          audio.currentTime = targetTime;
        }
      });
    }
  }

  function toggleSpeed() {
    const nextRate = playbackRate === 1 ? 1.5 : playbackRate === 1.5 ? 2 : 1;
    setPlaybackRate(nextRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = nextRate;
    }
  }

  const progressFraction = totalDuration > 0 ? Math.min(1, currentTime / totalDuration) : 0;
  const activeBarIndex = Math.floor(progressFraction * waveformBars.length);

  return (
    <div
      className={`flex items-center gap-2.5 py-1.5 px-1 min-w-[240px] max-w-[340px] select-none ${
        isMine ? 'text-gray-900' : 'text-gray-900'
      }`}
    >
      {/* Circular Play/Pause Button */}
      <button
        type="button"
        onClick={togglePlay}
        disabled={isLoading}
        className={`relative flex items-center justify-center w-10 h-10 rounded-full transition-transform active:scale-95 shrink-0 shadow-xs ${
          isMine
            ? 'bg-[#008665] text-white hover:bg-[#007054]'
            : 'bg-brand-600 text-white hover:bg-brand-700'
        }`}
        aria-label={isPlaying ? 'Pause voice message' : 'Play voice message'}
      >
        {isLoading ? (
          <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="3"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        ) : isPlaying ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="ml-0.5">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {/* Waveform and Progress Bar */}
      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5">
        <div
          ref={waveformRef}
          onClick={handleScrub}
          className="h-7 flex items-center gap-[2.5px] cursor-pointer group py-1"
          title="Click to seek"
        >
          {waveformBars.map((height, idx) => {
            const isPlayed = idx <= activeBarIndex && (isPlaying || currentTime > 0);
            return (
              <div
                key={idx}
                className="flex-1 rounded-full transition-colors duration-150"
                style={{
                  height: `${Math.round(height * 24)}px`,
                  minHeight: '4px',
                  backgroundColor: isPlayed
                    ? isMine
                      ? '#008665'
                      : '#0284c7'
                    : isMine
                    ? 'rgba(0, 0, 0, 0.22)'
                    : 'rgba(0, 0, 0, 0.25)',
                }}
              />
            );
          })}
        </div>

        {/* Footer info: Duration and Speed Toggle */}
        <div className="flex items-center justify-between text-[11px] font-mono leading-none text-gray-500">
          <span>{isPlaying || currentTime > 0 ? formatDuration(currentTime) : formatDuration(totalDuration)}</span>

          <div className="flex items-center gap-1.5">
            {loadError ? (
              <span className="text-red-500 font-sans text-[10px]">Error loading</span>
            ) : null}

            <button
              type="button"
              onClick={toggleSpeed}
              className={`px-1.5 py-0.5 rounded text-[10px] font-sans font-semibold transition-colors ${
                playbackRate > 1
                  ? isMine
                    ? 'bg-[#008665]/15 text-[#008665]'
                    : 'bg-brand-100 text-brand-700'
                  : 'bg-black/5 hover:bg-black/10 text-gray-600'
              }`}
              title="Toggle playback speed"
            >
              {playbackRate}x
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
